import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/button';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import type { Column } from '@/components/ui/data-table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { deploymentService, Release, Deployment } from '../../services/deploymentService';
import {
  reconcileDeployment, buildFleetRow, resolveTargetedDeviceIds, deploymentCreatedMs,
  isDeployableDevice,
  ReconciledDeployment, ReconciledDevice, FleetRow, FleetUpdateState, EffectiveDeviceState,
} from '../../services/deploymentReconcile';
import { edgeDeviceService } from '../../services/edgeDeviceService';
import { CentralEdgeDevice } from '@simcheck/shared-types';
import { useDateFormat } from '@/hooks/useDateFormat';
import {
  Rocket, Package, RefreshCw, CheckCircle, XCircle, ArrowDownToLine,
  RotateCcw, Loader2, Server, ChevronRight, AlertTriangle, MonitorSmartphone,
  WifiOff, Timer, MapPin
} from 'lucide-react';

type Tab = 'fleet' | 'deployments' | 'releases';

// History cards rendered per "Show more" click — keeps the DOM bounded when
// dozens/hundreds of deployment records accumulate.
const HISTORY_PAGE = 10;

function relativeTime(ms: number | null): string {
  if (ms == null) return 'never';
  const diff = Date.now() - ms;
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// Deploy Wizard Modal
function DeployWizard({ releases, onClose, onDeploy }: {
  releases: Release[];
  onClose: () => void;
  onDeploy: (params: any) => Promise<void>;
}) {
  const [step, setStep] = useState(1);
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [devices, setDevices] = useState<CentralEdgeDevice[]>([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [deployAll, setDeployAll] = useState(false);
  const [strategy, setStrategy] = useState<'all_at_once' | 'rolling'>('all_at_once');
  const [deploying, setDeploying] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);

  useEffect(() => {
    (async () => {
      setLoadingDevices(true);
      try {
        const devs = await edgeDeviceService.getDevices();
        // Real, approved hardware only — `isDeployableDevice` also drops the
        // synthetic demo fleet, which looks approved+online but has no Pi to
        // install anything (central-server rejects it as a target too).
        setDevices(devs.filter(isDeployableDevice));
      } catch (e) {
        console.error('Failed to load devices:', e);
      }
      setLoadingDevices(false);
    })();
  }, []);

  const toggleDevice = (id: string) => {
    setSelectedDeviceIds(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
    setDeployAll(false);
  };

  const handleDeploy = async () => {
    if (!selectedRelease) return;
    setDeploying(true);
    try {
      await onDeploy({
        releaseId: selectedRelease.id,
        targets: deployAll ? { all: true } : { deviceIds: selectedDeviceIds },
        strategy,
      });
      onClose();
    } catch (e) {
      console.error('Deploy failed:', e);
    }
    setDeploying(false);
  };

  const publishedReleases = releases.filter(r => r.status === 'published');

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Rocket className="w-5 h-5" /> Deploy Software Update
            </h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl">&times;</button>
          </div>
          <div className="flex items-center gap-2 mt-3">
            {[1, 2, 3].map(s => (
              <div key={s} className={`flex items-center gap-1 text-xs ${step >= s ? 'text-foreground' : 'text-muted-foreground/50'}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${step >= s ? 'bg-accent-blue text-white' : 'bg-muted text-muted-foreground'}`}>{s}</span>
                <span>{s === 1 ? 'Release' : s === 2 ? 'Targets' : 'Confirm'}</span>
                {s < 3 && <ChevronRight className="w-3 h-3 text-muted-foreground/50" />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Select a release to deploy:</p>
              {publishedReleases.length === 0 ? (
                <p className="text-sm text-muted-foreground/70">No published releases available.</p>
              ) : (
                publishedReleases.map(r => (
                  <button
                    key={r.id}
                    onClick={() => setSelectedRelease(r)}
                    className={`w-full text-left p-4 rounded-lg border transition-colors ${
                      selectedRelease?.id === r.id
                        ? 'border-accent-blue bg-accent-blue/10'
                        : 'border-border hover:border-muted-foreground/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <code className="text-sm font-semibold">v{r.version}</code>
                      {r.size > 0 && <span className="text-xs text-muted-foreground">{(r.size / 1024 / 1024).toFixed(1)} MB</span>}
                    </div>
                    {r.releaseNotes && <p className="text-xs text-muted-foreground mt-1">{r.releaseNotes}</p>}
                  </button>
                ))
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Select target devices:</p>
              <label className="flex items-center gap-2 p-3 rounded-lg border border-border hover:border-muted-foreground/30 cursor-pointer">
                <input type="checkbox" checked={deployAll} onChange={() => { setDeployAll(!deployAll); setSelectedDeviceIds([]); }} className="rounded" />
                <Server className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Deploy to all online devices ({devices.length})</span>
              </label>
              {!deployAll && (
                <div className="space-y-2 max-h-64 overflow-auto">
                  {loadingDevices ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground p-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading devices...</div>
                  ) : (
                    devices.map(d => (
                      <label key={d.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDeviceIds.includes(d.id)}
                          onChange={() => toggleDevice(d.id)}
                          className="rounded"
                        />
                        <span className="text-sm">{d.name}</span>
                        <span className="text-xs text-muted-foreground ml-1">({d.deviceId || d.device_id})</span>
                        <code className="text-xs text-muted-foreground ml-auto">{d.softwareVersion || (d as any).version || '?'}</code>
                      </label>
                    ))
                  )}
                </div>
              )}
              <div className="pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Strategy:</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setStrategy('all_at_once')}
                    className={`px-3 py-1.5 rounded text-xs font-medium border ${strategy === 'all_at_once' ? 'border-accent-blue bg-accent-blue/10 text-accent-light-blue' : 'border-border text-muted-foreground'}`}
                  >All at once</button>
                  <button
                    onClick={() => setStrategy('rolling')}
                    className={`px-3 py-1.5 rounded text-xs font-medium border ${strategy === 'rolling' ? 'border-accent-blue bg-accent-blue/10 text-accent-light-blue' : 'border-border text-muted-foreground'}`}
                  >Rolling</button>
                </div>
              </div>
            </div>
          )}

          {step === 3 && selectedRelease && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Review and confirm deployment:</p>
              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Release:</span><code>v{selectedRelease.version}</code></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Targets:</span><span>{deployAll ? `All online (${devices.length})` : `${selectedDeviceIds.length} devices`}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Strategy:</span><span className="capitalize">{strategy.replace('_', ' ')}</span></div>
                {selectedRelease.requirementsChanged && (
                  <div className="flex items-center gap-1 text-yellow-500 text-xs mt-2">
                    <AlertTriangle className="w-3 h-3" /> This release includes dependency changes
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex justify-between">
          <Button variant="ghost" onClick={step > 1 ? () => setStep(step - 1) : onClose}>
            {step > 1 ? 'Back' : 'Cancel'}
          </Button>
          {step < 3 ? (
            <Button
              variant="default"
              onClick={() => setStep(step + 1)}
              disabled={step === 1 ? !selectedRelease : step === 2 ? (!deployAll && selectedDeviceIds.length === 0) : false}
            >Next</Button>
          ) : (
            <Button variant="default" onClick={handleDeploy} disabled={deploying}>
              {deploying ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Deploying...</> : <><Rocket className="w-4 h-4 mr-1" /> Deploy</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fleet view (issue #134 primary surface): device-centric "what version is
// each device on right now, and is anything mid-update?".
// ---------------------------------------------------------------------------

const FLEET_STATE_META: Record<FleetUpdateState, { label: string; cls: string; icon: React.ReactNode }> = {
  up_to_date:    { label: 'Up to date', cls: 'text-emerald-500',     icon: <CheckCircle className="w-4 h-4 text-emerald-500" /> },
  updating:      { label: 'Updating',   cls: 'text-blue-500',        icon: <Loader2 className="w-4 h-4 text-blue-500 animate-spin" /> },
  stuck:         { label: 'Stuck',      cls: 'text-yellow-500',      icon: <Timer className="w-4 h-4 text-yellow-500" /> },
  update_failed: { label: 'Failed',     cls: 'text-destructive',     icon: <XCircle className="w-4 h-4 text-destructive" /> },
  offline:       { label: 'Offline',    cls: 'text-muted-foreground', icon: <WifiOff className="w-4 h-4 text-muted-foreground" /> },
};

// Attention order: devices needing action float to the top by default.
const FLEET_STATE_ORDER: Record<FleetUpdateState, number> = {
  update_failed: 0, stuck: 1, updating: 2, offline: 3, up_to_date: 4,
};

// Sortable scalar for a semver column: "4.10.1" → "0004.0010.0001" so string
// sort matches numeric segment order (plain lex sort puts 4.9 above 4.10).
function semverSortKey(v?: string): string | null {
  if (!v || v === '—') return null;
  return v.split('.').map((n) => String(parseInt(n, 10) || 0).padStart(4, '0')).join('.');
}

function FleetTab({ rows, summary }: { rows: FleetRow[]; summary: Record<FleetUpdateState, number> }) {
  // Chips double as state filters (click to toggle).
  const [stateFilter, setStateFilter] = useState<FleetUpdateState | null>(null);

  const visibleRows = useMemo(() => {
    const base = stateFilter ? rows.filter((r) => r.state === stateFilter) : rows;
    // Default order = attention-first; column-header sorts override client-side.
    return [...base].sort((a, b) =>
      FLEET_STATE_ORDER[a.state] - FLEET_STATE_ORDER[b.state] || a.name.localeCompare(b.name));
  }, [rows, stateFilter]);

  const columns: Column<FleetRow>[] = useMemo(() => [
    {
      key: 'name', header: 'Device', sortable: true,
      render: (_v, r) => (
        <div>
          <div className="font-medium text-foreground">{r.name}</div>
          <code className="text-xs text-muted-foreground">{r.deviceId}</code>
        </div>
      ),
    },
    {
      key: 'locationName', header: 'Location', sortable: true,
      render: (_v, r) => r.locationName
        ? <span className="inline-flex items-center gap-1 text-muted-foreground"><MapPin className="w-3 h-3" /> {r.locationName}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'currentVersion', header: 'Current', sortable: true,
      sortValue: (r) => semverSortKey(r.currentVersion),
      render: (_v, r) => <code className="text-xs">{r.currentVersion}</code>,
    },
    {
      key: 'desiredVersion', header: 'Desired', sortable: true,
      sortValue: (r) => semverSortKey(r.desiredVersion),
      render: (_v, r) => {
        const behind = r.desiredVersion && r.currentVersion !== r.desiredVersion;
        return r.desiredVersion
          ? <code className={`text-xs ${behind ? 'text-blue-500 font-semibold' : 'text-muted-foreground'}`}>{r.desiredVersion}</code>
          : <span className="text-xs text-muted-foreground/60">latest</span>;
      },
    },
    {
      key: 'state', header: 'State', sortable: true,
      sortValue: (r) => FLEET_STATE_ORDER[r.state],
      render: (_v, r) => {
        const meta = FLEET_STATE_META[r.state];
        return (
          <div>
            <span className={`inline-flex items-center gap-1.5 ${meta.cls}`}>
              {meta.icon}
              <span className="text-xs font-medium">{meta.label}</span>
            </span>
            {(r.state === 'updating' || r.state === 'stuck') && r.installPhase && (
              <div className="text-[11px] text-muted-foreground mt-0.5">{r.installPhase}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'lastSeenMs', header: 'Last seen', sortable: true,
      sortValue: (r) => r.lastSeenMs,
      render: (_v, r) => <span className="text-xs text-muted-foreground">{relativeTime(r.lastSeenMs)}</span>,
    },
  ], []);

  const chip = (state: FleetUpdateState | null, label: React.ReactNode, cls: string, count?: number) => {
    if (state !== null && (count ?? 0) === 0) return null;
    const active = stateFilter === state;
    return (
      <button
        onClick={() => setStateFilter(active ? null : state)}
        className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full transition-shadow ${cls} ${
          active ? 'ring-2 ring-ring' : 'hover:ring-1 hover:ring-ring/50'
        }`}
        title={state === null ? 'Show all devices' : `Filter to ${String(state).replace('_', ' ')} devices`}
      >
        {label}
      </button>
    );
  };

  if (rows.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Server className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No approved edge devices.</p>
        <p className="text-xs mt-1">Devices appear here once provisioned and approved.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary chips — clickable state filters */}
      <div className="flex flex-wrap gap-2">
        {chip(null, <><Server className="w-3.5 h-3.5 text-muted-foreground" /> {rows.length} device{rows.length === 1 ? '' : 's'}</>, 'bg-muted/50')}
        {chip('up_to_date', <><CheckCircle className="w-3.5 h-3.5" /> {summary.up_to_date} up to date</>, 'bg-emerald-500/10 text-emerald-500', summary.up_to_date)}
        {chip('updating', <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {summary.updating} updating</>, 'bg-blue-500/10 text-blue-500', summary.updating)}
        {chip('stuck', <><Timer className="w-3.5 h-3.5" /> {summary.stuck} stuck</>, 'bg-yellow-500/10 text-yellow-500', summary.stuck)}
        {chip('update_failed', <><XCircle className="w-3.5 h-3.5" /> {summary.update_failed} failed</>, 'bg-destructive/10 text-destructive', summary.update_failed)}
        {chip('offline', <><WifiOff className="w-3.5 h-3.5" /> {summary.offline} offline</>, 'bg-muted/50 text-muted-foreground', summary.offline)}
      </div>

      <ResponsiveTable<FleetRow>
        columns={columns}
        data={visibleRows}
        keyExtractor={(r) => r.deviceId}
        searchable
        searchPlaceholder="Search devices by name, ID, location, version..."
        searchKeys={['deviceId']}
        paginated
        defaultPageSize={25}
        emptyMessage={stateFilter ? `No ${String(stateFilter).replace('_', ' ')} devices.` : 'No devices.'}
        mobileCard={(r) => {
          const meta = FLEET_STATE_META[r.state];
          return (
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground truncate">{r.name}</span>
                <span className={`inline-flex items-center gap-1 text-xs shrink-0 ${meta.cls}`}>{meta.icon} {meta.label}</span>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <code>{r.currentVersion}</code>
                {r.desiredVersion && r.desiredVersion !== r.currentVersion && (
                  <>→ <code className="text-blue-500">{r.desiredVersion}</code></>
                )}
                <span className="ml-auto">{relativeTime(r.lastSeenMs)}</span>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deployment history card — reconciled against authoritative device state so
// it reaches a terminal headline instead of spinning forever (#134).
// ---------------------------------------------------------------------------

const EFFECTIVE_DEVICE_META: Record<EffectiveDeviceState, { label: string; cls: string; icon: React.ReactNode }> = {
  up_to_date: { label: 'on target', cls: 'text-emerald-500',     icon: <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> },
  superseded: { label: 'superseded', cls: 'text-muted-foreground', icon: <CheckCircle className="w-3.5 h-3.5 text-muted-foreground" /> },
  updating:   { label: 'updating',  cls: 'text-blue-500',        icon: <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" /> },
  failed:     { label: 'failed',    cls: 'text-destructive',     icon: <XCircle className="w-3.5 h-3.5 text-destructive" /> },
  timed_out:  { label: 'timed out', cls: 'text-yellow-500',      icon: <Timer className="w-3.5 h-3.5 text-yellow-500" /> },
  offline:    { label: 'offline',   cls: 'text-muted-foreground', icon: <WifiOff className="w-3.5 h-3.5 text-muted-foreground" /> },
};

function effectiveHeadline(status: ReconciledDeployment['effectiveStatus']) {
  switch (status) {
    case 'completed':   return { badge: 'secondary' as const, label: 'completed', icon: <CheckCircle className="w-4 h-4 text-emerald-500" /> };
    case 'superseded':  return { badge: 'outline' as const, label: 'superseded', icon: <CheckCircle className="w-4 h-4 text-muted-foreground" /> };
    case 'failed':      return { badge: 'destructive' as const, label: 'failed', icon: <XCircle className="w-4 h-4 text-destructive" /> };
    case 'stalled':     return { badge: 'outline' as const, label: 'stalled', icon: <AlertTriangle className="w-4 h-4 text-yellow-500" /> };
    case 'in_progress':
    default:            return { badge: 'default' as const, label: 'in progress', icon: <Loader2 className="w-4 h-4 text-blue-500 animate-spin" /> };
  }
}

function ReconciledDeploymentCard({ rec, onRollback, onViewDevices }: {
  rec: ReconciledDeployment;
  onRollback: (id: string) => void;
  onViewDevices: (rec: ReconciledDeployment, blockersOnly?: boolean) => void;
}) {
  const { deployment, counts, total, effectiveStatus } = rec;
  const fmt = useDateFormat();
  const head = effectiveHeadline(effectiveStatus);
  // "Done" = on the target version OR already past it via a later deployment.
  const satisfied = counts.up_to_date + counts.superseded;
  const donePct = total > 0 ? Math.round((satisfied / total) * 100) : 0;
  const blocked = counts.failed + counts.timed_out + counts.offline;

  return (
    <div className="border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {head.icon}
          <code className="text-sm font-semibold">v{deployment.releaseVersion}</code>
          <Badge variant={head.badge} className="text-xs">{head.label}</Badge>
          {effectiveStatus !== deployment.status && (
            <span className="text-[11px] text-muted-foreground/60" title="Raw backend status, reconciled against live device state">
              (raw: {deployment.status})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Rollback while devices are still moving — and for a stalled,
              partially-applied rollout, which is exactly when an operator
              may want to back out. */}
          {(effectiveStatus === 'in_progress' || effectiveStatus === 'stalled') && (
            <Button variant="destructive" size="sm" onClick={() => onRollback(deployment.id)}>
              <RotateCcw className="w-3 h-3 mr-1" /> Rollback
            </Button>
          )}
          <span className="text-xs text-muted-foreground">
            {deployment.createdAt?._seconds
              ? fmt.formatDateTime(deployment.createdAt._seconds * 1000)
              : ''}
          </span>
        </div>
      </div>

      {/* Progress bar — fraction of targeted devices actually on the target version */}
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-2">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${donePct}%` }} />
      </div>
      {/* Summary counts only — NEVER per-device chips here. Device detail is a
          drill-down table (sheet) so the card stays the same size whether the
          deployment targeted 7 devices or 700. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="text-emerald-500">{counts.up_to_date} on target</span>
        {counts.superseded > 0 && <span>{counts.superseded} superseded</span>}
        {counts.updating > 0 && <span className="text-blue-500">{counts.updating} updating</span>}
        {counts.failed > 0 && <span className="text-destructive">{counts.failed} failed</span>}
        {counts.timed_out > 0 && <span className="text-yellow-500">{counts.timed_out} timed out</span>}
        {counts.offline > 0 && <span>{counts.offline} offline</span>}
        <button
          onClick={() => onViewDevices(rec)}
          className="ml-auto inline-flex items-center gap-1 text-foreground/80 hover:text-foreground font-medium"
          title="Open the per-device breakdown for this deployment"
        >
          {total} targeted <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* "What's blocking completion" — counts summary; click through for the
          device-level table filtered to the blockers. */}
      {effectiveStatus === 'stalled' && (
        <button
          onClick={() => onViewDevices(rec, true)}
          className="mt-2 w-full flex items-start gap-1.5 text-left text-xs text-yellow-500/90 bg-yellow-500/10 border border-yellow-500/20 rounded p-2 hover:bg-yellow-500/15"
          title="Show the blocked devices"
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Blocked: {blocked} device{blocked === 1 ? '' : 's'} won't converge
            {counts.offline > 0 && ` — ${counts.offline} offline (will converge on reconnect)`}
            {counts.timed_out > 0 && `${counts.offline > 0 ? ',' : ' —'} ${counts.timed_out} timed out`}
            {counts.failed > 0 && `${counts.offline || counts.timed_out ? ',' : ' —'} ${counts.failed} failed`}.
          </span>
          <ChevronRight className="w-3.5 h-3.5 mt-0.5 ml-auto shrink-0" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-deployment device drill-down — a real table (search / sort / filter /
// pagination via the shared DataTable), not an inline chip dump. Scales to
// hundreds of targeted devices.
// ---------------------------------------------------------------------------

const ATTENTION_STATES: EffectiveDeviceState[] = ['failed', 'timed_out', 'offline', 'updating'];
const DEVICE_STATE_ORDER: Record<EffectiveDeviceState, number> = {
  failed: 0, timed_out: 1, updating: 2, offline: 3, up_to_date: 4, superseded: 5,
};

function DeploymentDevicesSheet({ rec, blockersOnly, onClose }: {
  rec: ReconciledDeployment;
  blockersOnly: boolean;
  onClose: () => void;
}) {
  const [stateFilter, setStateFilter] = useState<EffectiveDeviceState | 'blocked' | null>(
    blockersOnly ? 'blocked' : null,
  );

  const rows = useMemo(() => {
    const base = stateFilter === 'blocked'
      ? rec.devices.filter((d) => ATTENTION_STATES.includes(d.effective))
      : stateFilter
        ? rec.devices.filter((d) => d.effective === stateFilter)
        : rec.devices;
    // Attention-first; header sorts override.
    return [...base].sort((a, b) =>
      DEVICE_STATE_ORDER[a.effective] - DEVICE_STATE_ORDER[b.effective] || a.name.localeCompare(b.name));
  }, [rec.devices, stateFilter]);

  const columns: Column<ReconciledDevice>[] = useMemo(() => [
    {
      key: 'name', header: 'Device', sortable: true,
      render: (_v, d) => (
        <div>
          <div className="font-medium text-foreground">{d.name}</div>
          <code className="text-xs text-muted-foreground">{d.deviceId}</code>
        </div>
      ),
    },
    {
      key: 'effective', header: 'State', sortable: true,
      sortValue: (d) => DEVICE_STATE_ORDER[d.effective],
      render: (_v, d) => {
        const meta = EFFECTIVE_DEVICE_META[d.effective];
        return <span className={`inline-flex items-center gap-1.5 text-xs ${meta.cls}`}>{meta.icon} {meta.label}</span>;
      },
    },
    {
      key: 'reportedVersion', header: 'Reported', sortable: true,
      sortValue: (d) => semverSortKey(d.reportedVersion),
      render: (_v, d) => d.reportedVersion
        ? <code className="text-xs">{d.reportedVersion}</code>
        : <span className="text-xs text-muted-foreground/60">—</span>,
    },
    {
      key: 'installPhase', header: 'Detail',
      render: (_v, d) => (d.error || d.installPhase)
        ? <span className="text-xs text-muted-foreground" title={d.error}>{d.error || d.installPhase}</span>
        : <span className="text-xs text-muted-foreground/40">—</span>,
    },
  ], []);

  const filterChip = (
    key: EffectiveDeviceState | 'blocked' | null,
    label: string,
    count: number,
    cls: string,
  ) => {
    if (key !== null && count === 0) return null;
    const active = stateFilter === key;
    return (
      <button
        key={String(key)}
        onClick={() => setStateFilter(active ? null : key)}
        className={`text-xs px-2.5 py-1 rounded-full transition-shadow ${cls} ${
          active ? 'ring-2 ring-ring' : 'hover:ring-1 hover:ring-ring/50'
        }`}
      >
        {count} {label}
      </button>
    );
  };

  const blockedCount = rec.devices.filter((d) => ATTENTION_STATES.includes(d.effective)).length;

  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Rocket className="w-4 h-4" />
            v{rec.deployment.releaseVersion} — targeted devices
          </SheetTitle>
          <SheetDescription>
            Per-device state for this deployment, reconciled against the live registry.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            {filterChip(null, 'targeted', rec.total, 'bg-muted/50 text-foreground/80')}
            {filterChip('blocked', 'blocked', blockedCount, 'bg-yellow-500/10 text-yellow-500')}
            {filterChip('up_to_date', 'on target', rec.counts.up_to_date, 'bg-emerald-500/10 text-emerald-500')}
            {filterChip('superseded', 'superseded', rec.counts.superseded, 'bg-muted/50 text-muted-foreground')}
            {filterChip('updating', 'updating', rec.counts.updating, 'bg-blue-500/10 text-blue-500')}
            {filterChip('failed', 'failed', rec.counts.failed, 'bg-destructive/10 text-destructive')}
            {filterChip('timed_out', 'timed out', rec.counts.timed_out, 'bg-yellow-500/10 text-yellow-500')}
            {filterChip('offline', 'offline', rec.counts.offline, 'bg-muted/50 text-muted-foreground')}
          </div>

          <ResponsiveTable<ReconciledDevice>
            columns={columns}
            data={rows}
            keyExtractor={(d) => d.deviceId}
            searchable
            searchPlaceholder="Search devices..."
            searchKeys={['deviceId']}
            paginated
            defaultPageSize={25}
            emptyMessage="No devices match."
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Create Release Modal
function CreateReleaseModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [version, setVersion] = useState('');
  const [gcsPath, setGcsPath] = useState('');
  const [checksum, setChecksum] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [requirementsChanged, setRequirementsChanged] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!version || !gcsPath || !checksum) return;
    setCreating(true);
    try {
      await deploymentService.createRelease({
        version,
        artifactGcsPath: gcsPath,
        checksum,
        releaseNotes,
        requirementsChanged,
        size: 0,
      });
      onCreated();
      onClose();
    } catch (e) {
      console.error('Failed to create release:', e);
    }
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-lg">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Package className="w-5 h-5" /> Create Release
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl">&times;</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">Version *</label>
            <input value={version} onChange={e => setVersion(e.target.value)} placeholder="2.0.3"
              className="mt-1 w-full px-3 py-2 bg-muted border border-border rounded text-sm text-foreground" />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">GCS Artifact Path *</label>
            <input value={gcsPath} onChange={e => setGcsPath(e.target.value)} placeholder="gs://simcheck-releases/v2.0.3/modem-gateway-v2.0.3.tar.gz"
              className="mt-1 w-full px-3 py-2 bg-muted border border-border rounded text-sm text-foreground font-mono text-xs" />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">SHA-256 Checksum *</label>
            <input value={checksum} onChange={e => setChecksum(e.target.value)} placeholder="sha256:abc123..."
              className="mt-1 w-full px-3 py-2 bg-muted border border-border rounded text-sm text-foreground font-mono text-xs" />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Release Notes</label>
            <textarea value={releaseNotes} onChange={e => setReleaseNotes(e.target.value)} rows={2} placeholder="What changed..."
              className="mt-1 w-full px-3 py-2 bg-muted border border-border rounded text-sm text-foreground" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={requirementsChanged} onChange={e => setRequirementsChanged(e.target.checked)} className="rounded" />
            Requirements changed (triggers pip install on devices)
          </label>
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="default" onClick={handleCreate} disabled={creating || !version || !gcsPath || !checksum}>
            {creating ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creating...</> : 'Create Release'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Deployments() {
  const [activeTab, setActiveTab] = useState<Tab>('fleet');
  const [releases, setReleases] = useState<Release[]>([]);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [devices, setDevices] = useState<CentralEdgeDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [showCreateRelease, setShowCreateRelease] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [rels, deps, devs] = await Promise.all([
        deploymentService.getReleases(),
        deploymentService.getDeployments(),
        edgeDeviceService.getDevices(true),
      ]);
      setReleases(rels);
      setDeployments(deps);
      setDevices(devs);
    } catch (e) {
      console.error('Failed to load deployment data:', e);
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // The real, approved (non-demo) fleet — the deterministic device universe
  // that both the Fleet view and deployment reconciliation derive from.
  // `isDeployableDevice` additionally excludes the synthetic demo fleet: it
  // reads approved + permanently online (a cron re-stamps its heartbeat) but
  // has no Pi to install anything, so it can only ever add phantom rows.
  const approvedDevices = useMemo(
    () => devices.filter(isDeployableDevice),
    [devices],
  );

  const devicesById = useMemo(() => {
    const m = new Map<string, CentralEdgeDevice>();
    for (const d of devices) {
      m.set(d.deviceId || d.id, d);
      if (d.id && d.id !== (d.deviceId || d.id)) m.set(d.id, d);
    }
    return m;
  }, [devices]);

  const fleetRows: FleetRow[] = useMemo(() => {
    const now = Date.now();
    return approvedDevices.map(d => {
      // When was this device's current pin written? Newest deployment record
      // for the pinned version that targeted the device. Lets the fleet row
      // time out to `stuck` even when the install never started (no
      // installPhaseStartedAt) instead of "updating" forever.
      let pinnedSinceMs: number | null = null;
      if (d.desiredVersion) {
        const did = d.deviceId || d.id;
        for (const dep of deployments) {
          if (dep.releaseVersion !== d.desiredVersion) continue;
          const created = deploymentCreatedMs(dep);
          if (created === 0) continue;
          const ids = resolveTargetedDeviceIds(dep, approvedDevices, devicesById);
          if (!ids.includes(did) && !ids.includes(d.id)) continue;
          if (pinnedSinceMs == null || created > pinnedSinceMs) pinnedSinceMs = created;
        }
      }
      return buildFleetRow(d, now, { pinnedSinceMs });
    });
  }, [approvedDevices, deployments, devicesById]);

  const reconciledDeployments: ReconciledDeployment[] = useMemo(
    () => deployments.map(d => reconcileDeployment(d, devicesById, approvedDevices)),
    [deployments, devicesById, approvedDevices],
  );

  // Fleet headline counters.
  const fleetSummary = useMemo(() => {
    const s: Record<FleetUpdateState, number> = { up_to_date: 0, updating: 0, stuck: 0, update_failed: 0, offline: 0 };
    for (const r of fleetRows) s[r.state] += 1;
    return s;
  }, [fleetRows]);

  // Auto-refresh while anything is genuinely still moving — based on the
  // RECONCILED status, not the raw (possibly-stuck) `in_progress` flag, so a
  // permanently-stalled deployment no longer pins a 5s polling loop forever.
  // Fleet rows past the convergence timeout resolve to `stuck` (not
  // `updating`), so they don't pin the poll either.
  useEffect(() => {
    const moving = reconciledDeployments.some(r => r.effectiveStatus === 'in_progress')
      || fleetRows.some(r => r.state === 'updating');
    if (!moving) return;
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [reconciledDeployments, fleetRows, loadData]);

  const [rollbackTarget, setRollbackTarget] = useState<string | null>(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(HISTORY_PAGE);
  // Device drill-down overlay (Sheet) for one deployment — device detail never
  // renders inline on the page.
  const [deviceSheet, setDeviceSheet] = useState<{ rec: ReconciledDeployment; blockersOnly: boolean } | null>(null);
  const openDeviceSheet = useCallback(
    (rec: ReconciledDeployment, blockersOnly = false) => setDeviceSheet({ rec, blockersOnly }),
    [],
  );

  const handleDeploy = async (params: any) => {
    await deploymentService.deploy(params);
    await loadData();
  };

  const confirmRollback = async () => {
    if (!rollbackTarget) return;
    setRollingBack(true);
    try {
      await deploymentService.rollback(rollbackTarget);
      await loadData();
    } catch (e) {
      console.error('Rollback failed:', e);
    }
    setRollingBack(false);
    setRollbackTarget(null);
  };

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Deployments</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Track each device's current vs. desired version, and deploy software updates
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setRefreshing(true); loadData(); }}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="default" size="sm" onClick={() => setShowWizard(true)}>
              <Rocket className="w-4 h-4 mr-1" /> New Deployment
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex -mb-px">
            {(['fleet', 'deployments', 'releases'] as Tab[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                  activeTab === tab
                    ? 'border-accent-blue text-accent-light-blue'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab === 'fleet'
                  ? <><MonitorSmartphone className="w-4 h-4 inline mr-1" /> Fleet</>
                  : tab === 'deployments'
                    ? <><Rocket className="w-4 h-4 inline mr-1" /> History</>
                    : <><Package className="w-4 h-4 inline mr-1" /> Releases</>}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading...
          </div>
        ) : (
          <>
            {activeTab === 'fleet' && (
              <div className="space-y-4">
                <FleetTab rows={fleetRows} summary={fleetSummary} />
                <p className="text-xs text-muted-foreground/70">
                  Versions are read from each device's live heartbeat. "Desired" is the version pinned
                  for that device; offline devices converge on reconnect.
                </p>
              </div>
            )}

            {activeTab === 'deployments' && (
              <div className="space-y-4">
                {reconciledDeployments.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Rocket className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No deployments yet.</p>
                    <p className="text-xs mt-1">Create a release and deploy it to your edge devices.</p>
                  </div>
                ) : (
                  <>
                    {reconciledDeployments.slice(0, historyVisible).map(rec => (
                      <ReconciledDeploymentCard key={rec.deployment.id} rec={rec} onRollback={setRollbackTarget} onViewDevices={openDeviceSheet} />
                    ))}
                    {reconciledDeployments.length > historyVisible && (
                      <div className="flex justify-center">
                        <Button variant="outline" size="sm" onClick={() => setHistoryVisible(v => v + HISTORY_PAGE)}>
                          Show {Math.min(HISTORY_PAGE, reconciledDeployments.length - historyVisible)} more
                          <span className="text-muted-foreground ml-1.5">
                            ({historyVisible} of {reconciledDeployments.length})
                          </span>
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'releases' && (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={() => setShowCreateRelease(true)}>
                    <Package className="w-4 h-4 mr-1" /> Create Release
                  </Button>
                </div>
                {releases.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No releases yet.</p>
                    <p className="text-xs mt-1">Run <code>./scripts/deploy/release.sh</code> or create one manually.</p>
                  </div>
                ) : (
                  releases.map(r => (
                    <div key={r.id} className="border border-border rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-muted-foreground" />
                          <code className="text-sm font-semibold">v{r.version}</code>
                          <Badge variant={r.status === 'published' ? 'secondary' : r.status === 'deprecated' ? 'destructive' : 'outline'} className="text-xs">
                            {r.status}
                          </Badge>
                          {r.requirementsChanged && (
                            <Badge variant="outline" className="text-xs text-yellow-500 border-yellow-500/30">deps changed</Badge>
                          )}
                        </div>
                        {r.releaseNotes && <p className="text-xs text-muted-foreground mt-1">{r.releaseNotes}</p>}
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground/70">
                          {r.size > 0 && <span>{(r.size / 1024 / 1024).toFixed(1)} MB</span>}
                          <span>{r.checksum?.substring(0, 24)}...</span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setShowWizard(true); }}
                        disabled={r.status !== 'published'}
                      >
                        <ArrowDownToLine className="w-3 h-3 mr-1" /> Deploy
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        {/* Deploy Wizard */}
        {showWizard && (
          <DeployWizard
            releases={releases}
            onClose={() => setShowWizard(false)}
            onDeploy={handleDeploy}
          />
        )}

        {showCreateRelease && (
          <CreateReleaseModal
            onClose={() => setShowCreateRelease(false)}
            onCreated={loadData}
          />
        )}

        {/* Per-deployment device drill-down (overlay Sheet, never inline) */}
        {deviceSheet && (
          <DeploymentDevicesSheet
            rec={deviceSheet.rec}
            blockersOnly={deviceSheet.blockersOnly}
            onClose={() => setDeviceSheet(null)}
          />
        )}

        {/* Rollback confirmation (no browser confirm() — custom modal per UI rules) */}
        {rollbackTarget && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md">
              <div className="p-6 border-b border-border flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-destructive" />
                <h2 className="text-lg font-semibold text-foreground">Roll back deployment?</h2>
              </div>
              <div className="p-6 text-sm text-muted-foreground">
                Targeted devices will be pinned back to their previous version. Devices already on the
                new version will revert on their next update poll.
              </div>
              <div className="p-4 border-t border-border flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setRollbackTarget(null)} disabled={rollingBack}>Cancel</Button>
                <Button variant="destructive" onClick={confirmRollback} disabled={rollingBack}>
                  {rollingBack ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Rolling back...</> : 'Roll back'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
